import BaseService from "./BaseService";
import i18n from "i18next";
import Config from "./Config";

/**
 * Idiomas en los que se publica la app.
 *
 * Es el conjunto de claves de languajecodes, y lo que devuelve siempre
 * filterLangValue: cualquier otro codigo cae a 'en'.
 */
export type Language = 'es' | 'en';

export class LocaleService extends BaseService {
    /**
    * Base URL for translating Gronze links
    */
    static gronzeTranslateBaseUrl = 'https://www-gronze-com.translate.goog';

    /*
    *
     */
    static languajecodes: Record<Language, string> = {
        'es': 'es-ES',
        'en': 'en-US',
    }

    /**
     * Get the current language from i18n
     */
    static getLanguage(): Language {
        return this.filterLangValue(i18n.language);
    }

    /**
     * Set the current language in i18n
     */
    static setLanguage(language: string): void {
        i18n.changeLanguage(language);
    }

    /**
     * Translate a Gronze link if the current language is not Spanish
     */
    static translateGronzeLink(url: string): string {
        const lang = this.filterLangValue(i18n.language);
        if (lang === 'es')
            return url;

        const [baseUrl, originalQuery] = url.split('?');
        const newUrl = baseUrl.replace(Config.getRestUrl(), this.gronzeTranslateBaseUrl);
        const translateParams = `_x_tr_sl=es&_x_tr_tl=${lang}&_x_tr_hl=${this.languajecodes[lang]}`;
        let finalUrl = `${newUrl}?${translateParams}`;
        if (originalQuery) {
            finalUrl += `&${originalQuery}`;
        }
        return finalUrl;
    }

    /**
     * Decode HTML entities received from backend
     *
     * @returns returns de decoded string value
     */
    static decodeHtmlEntities(str: string): string {
        const txt = document.createElement('textarea');
        txt.innerHTML = str;
        // Los signos de cierre no llevan espacio delante; los de apertura
        // (¿ ¡) si lo llevan, y lo que sobra es el espacio de detras.
        return txt.value
            .replace(/\s+([.,:;!?])/g, '$1')
            .replace(/([¿¡])\s+/g, '$1');
    }

    /**
     * Normaliza un codigo de idioma al conjunto que soporta la app.
     *
     * Acepta tambien un array porque asi lo entrega el fallbackLng de i18next.
     * @see src/i18n.js
     */
    static filterLangValue(code?: string | readonly string[] | null): Language {
        if (!code) return 'en';
        const value = typeof code === 'string' ? code : code[0];
        const lang = value.split('-')[0];
        if (['ca', 'gl', 'eu'].includes(lang)) return 'es';
        // La comprobacion contra las claves es la validacion: lo que pase de
        // aqui es una de ellas, aunque TypeScript no pueda deducirlo.
        if (Object.keys(LocaleService.languajecodes).includes(lang)) return lang as Language;
        return 'en';
    }
}
